// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { formatDate } from '@loa/theme';
import { Modal, StatusBanner, Select, AppIcon, Button, IconButton, Spinner, Badge, type SelectOption } from '@loa/ui-components';
import type { Policy, PolicyRelationship, RelationshipType } from '../types/policy';
import type { Client } from '../types/client';
import { policyApi } from '../api/policyApi';

type PolicySignatoriesProps = Readonly<{
  policy: Policy;
  clients: Client[];
  onClose: () => void;
  onUpdate: () => void;
}>;

export function PolicySignatories({
  policy,
  clients,
  onClose,
  onUpdate
}: PolicySignatoriesProps) {
  const [relationships, setRelationships] = useState<PolicyRelationship[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('OWNER_AND_SIGNATORY');

  useEffect(() => {
    loadRelationships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy.id]);

  const loadRelationships = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await policyApi.getRelationships(policy.id);
      setRelationships(data);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to load relationships');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelationship = async () => {
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await policyApi.createRelationship(policy.id, {
        clientProfileId: selectedClientId,
        relationshipType
      });
      await loadRelationships();
      setShowAddModal(false);
      setSelectedClientId('');
      setRelationshipType('OWNER_AND_SIGNATORY');
      onUpdate();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to add relationship');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelationship = async (relationshipId: string) => {
    if (!confirm('Are you sure you want to remove this relationship?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await policyApi.deleteRelationship(policy.id, relationshipId);
      await loadRelationships();
      onUpdate();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to delete relationship');
    } finally {
      setLoading(false);
    }
  };

  const getRelationshipTypeLabel = (type: RelationshipType) => {
    switch (type) {
      case 'OWNER':
        return { color: 'blue', text: 'Owner', icon: 'user' };
      case 'SIGNATORY':
        return { color: 'green', text: 'Signatory', icon: 'pencil' };
      case 'OWNER_AND_SIGNATORY':
        return { color: 'purple', text: 'Owner & Signatory', icon: 'users' };
      default:
        return { color: 'grey', text: type, icon: 'question' };
    }
  };

  const availableClients = clients.filter(
    client => !relationships.some(rel => rel.clientId === client.id)
  );

  const clientOptions: SelectOption[] = availableClients.map(client => ({
    value: client.id,
    text: `${client.firstName} ${client.lastName} (${client.email})`,
  }));

  const relationshipTypeOptions: SelectOption[] = [
    { value: 'OWNER', text: 'Owner' },
    { value: 'SIGNATORY', text: 'Signatory' },
    { value: 'OWNER_AND_SIGNATORY', text: 'Owner & Signatory' }
  ];

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header
        icon="users"
        title={`Manage Policy Relationships - ${policy.policyNumber}`}
        onClose={onClose}
      />

      <Modal.Content>
        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div style={{ marginBottom: '1rem' }}>
          <Button
            variant="filled"
            onClick={() => setShowAddModal(true)}
            disabled={availableClients.length === 0}
            iconLeft={<AppIcon name='plus' />}
          >
            Add Relationship
          </Button>
        </div>

        {loading && <Spinner />}

        {!loading && relationships.length === 0 && (
          <p style={{ color: '#767676', textAlign: 'center', padding: '2rem 0' }}>
            No clients are associated with this policy yet.
          </p>
        )}

        {!loading && relationships.length > 0 && (
          <table className="fin-table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Email</th>
                <th>Relationship Type</th>
                <th>Added</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {relationships.map((relationship) => {
                const typeInfo = getRelationshipTypeLabel(relationship.relationshipType);
                return (
                  <tr key={relationship.id}>
                    <td>{relationship.clientName}</td>
                    <td>{relationship.clientEmail}</td>
                    <td>
                      <Badge color={typeInfo.color as 'blue' | 'green' | 'purple' | 'grey'}>
                        <AppIcon name={typeInfo.icon as 'user' | 'pencil' | 'users' | 'question'} />
                        {typeInfo.text}
                      </Badge>
                    </td>
                    <td>
                      {formatDate(relationship.createdAt)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRelationship(relationship.id)}
                        aria-label='Remove relationship'
                        icon={<AppIcon name='trash' />}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <Modal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          size="medium"
        >
          <Modal.Header title="Add Policy Relationship" onClose={() => setShowAddModal(false)} />
          <Modal.Content>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Select
                label="Select Client"
                placeholder="Choose a client"
                options={clientOptions}
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                required
                fluid
              />
              <Select
                label="Relationship Type"
                options={relationshipTypeOptions}
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value as RelationshipType)}
                required
                fluid
              />
            </div>
          </Modal.Content>
          <Modal.Actions
            secondaryAction={{ label: 'Cancel', onClick: () => setShowAddModal(false) }}
            primaryAction={{ label: 'Add Relationship', onClick: () => { void handleAddRelationship(); }, loading, disabled: !selectedClientId }}
          />
        </Modal>
      </Modal.Content>

      <Modal.Actions
        primaryAction={{ label: 'Close', onClick: onClose }}
      />
    </Modal>
  );
}
